import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import {
  useListSupportTicketsQuery,
  type SupportTicketRow,
} from "@/features/accounts/accountsApi";
import SupportCaseThread from "./SupportCaseThread";
import { isLowCsat } from "./SupportCsatSummary";
import { DetailSection } from "./ClientDetailsFields";

/**
 * Owner lifecycle: OPEN → CLOSED. Closed is FINAL (no reopen).
 * `in_progress` is an Open sub-state (Support Mode active). `resolved` is the
 * legacy persisted spelling of Closed — historical rows are never rewritten
 * and always read/display as Closed.
 */
export type SupportCaseListFilter = "all" | "open" | "closed" | "lowSatisfaction";

/** Live refresh cadence for the Case list (RTK Query polling; no new subsystem). */
export const SUPPORT_LIST_POLL_MS = 15_000;

export function formatSupportStatusLabel(status?: string | null): string {
  const raw = String(status || "").trim();
  if (!raw) return "Unknown";
  if (isClosedSupportStatus(raw)) return "Closed";
  return raw
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function formatSupportWhen(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

/** Canonical closure actor (server-projected): Client-initiated close. */
export function isClientClosedCase(ticket: SupportTicketRow): boolean {
  return (
    Boolean(ticket.closedByClient) ||
    String(ticket.closedByType || "").toLowerCase() === "client"
  );
}

/**
 * Human-readable closer only — never render Auth0 subs in primary UI.
 *   staff  → "Alex Chen" / "KrossKast Support"
 *   client → "Teddy Boom (Client)" / "Client"   (Owner decision: Client close)
 */
export function saClosedByLabel(ticket: SupportTicketRow): string | null {
  const name = String(ticket.closedByName || ticket.resolvedByName || "").trim();
  if (isClientClosedCase(ticket)) return name ? `${name} (Client)` : "Client";
  if (name) return name;
  if (
    ticket.closedAt ||
    ticket.resolvedAt ||
    ticket.closureNote ||
    ticket.resolutionNote
  ) {
    return "KrossKast Support";
  }
  return null;
}
/** @deprecated legacy name — use saClosedByLabel. */
export const saResolvedByLabel = saClosedByLabel;

export function ticketClosedAt(ticket: SupportTicketRow): string | null {
  return ticket.closedAt || ticket.resolvedAt || null;
}

export function ticketClosureNote(ticket: SupportTicketRow): string | null {
  return ticket.closureNote || ticket.resolutionNote || null;
}

export function isOpenSupportStatus(status?: string | null): boolean {
  const s = String(status || "").toLowerCase();
  return s === "open" || s === "in_progress";
}

export function isClosedSupportStatus(status?: string | null): boolean {
  const s = String(status || "").toLowerCase();
  return s === "resolved" || s === "closed";
}
/** @deprecated legacy name — use isClosedSupportStatus. */
export const isResolvedSupportStatus = isClosedSupportStatus;

/**
 * ONE Case ID = ONE case. Deduplicate by ticketId keeping the first
 * (API should already return unique META rows; this is belt-and-suspenders).
 */
export function uniqueCanonicalSupportCases(
  rows: SupportTicketRow[] | null | undefined,
): SupportTicketRow[] {
  const seen = new Set<string>();
  const out: SupportTicketRow[] = [];
  for (const row of rows || []) {
    const id = String(row?.ticketId || "").trim();
    if (!id || seen.has(id)) continue;
    // Drop message-projection phantoms (no status/summary) if they ever leak.
    if (!row.status && !row.summary) continue;
    seen.add(id);
    out.push(row);
  }
  return out;
}

export function countSupportCaseBuckets(cases: SupportTicketRow[]) {
  let open = 0;
  let closed = 0;
  let lowCsat = 0;
  for (const c of cases) {
    if (isOpenSupportStatus(c.status)) open += 1;
    else if (isClosedSupportStatus(c.status)) closed += 1;
    if (c.csatLowSatisfaction || isLowCsat(c.csatRating)) lowCsat += 1;
  }
  return { total: cases.length, open, closed, lowCsat };
}

export function isLowSatisfactionCase(c: SupportTicketRow): boolean {
  return Boolean(c.csatLowSatisfaction) || isLowCsat(c.csatRating);
}

export function filterSupportCasesByBucket(
  cases: SupportTicketRow[],
  filter: SupportCaseListFilter,
): SupportTicketRow[] {
  if (filter === "open") return cases.filter((c) => isOpenSupportStatus(c.status));
  if (filter === "closed")
    return cases.filter((c) => isClosedSupportStatus(c.status));
  if (filter === "lowSatisfaction") return cases.filter(isLowSatisfactionCase);
  return cases;
}

const parseTs = (v?: string | null): number => {
  const n = Date.parse(String(v || ""));
  return Number.isFinite(n) ? n : 0;
};
const cmpDesc = <T extends number | string>(a: T, b: T) =>
  a === b ? 0 : a > b ? -1 : 1;

/**
 * Owner-canonical ordering per view (mirrors the API's
 * `sortSupportTicketsForView` so SA and MA agree):
 *   all / open      → most recently active (updatedAt, else createdAt) first
 *   closed          → most recently closed (closedAt / legacy resolvedAt) first
 *   lowSatisfaction → most recently submitted low-CSAT response first
 * Deterministic tie-breaks: activity DESC, createdAt DESC, ticketId DESC.
 * `clientCaseNumber` ("Case: 01") is display-only and never drives position.
 */
export function sortSupportCasesForView(
  cases: SupportTicketRow[],
  filter: SupportCaseListFilter,
): SupportTicketRow[] {
  const activity = (t: SupportTicketRow) =>
    parseTs(t.updatedAt) || parseTs(t.createdAt);
  const closedAt = (t: SupportTicketRow) =>
    parseTs(t.closedAt) || parseTs(t.resolvedAt) || activity(t);
  const csatAt = (t: SupportTicketRow) => parseTs(t.csatSubmittedAt);
  const primary =
    filter === "closed"
      ? closedAt
      : filter === "lowSatisfaction"
        ? csatAt
        : activity;
  return [...cases].sort(
    (a, b) =>
      cmpDesc(primary(a), primary(b)) ||
      cmpDesc(activity(a), activity(b)) ||
      cmpDesc(parseTs(a.createdAt), parseTs(b.createdAt)) ||
      cmpDesc(String(a.ticketId || ""), String(b.ticketId || "")),
  );
}

export function formatClientCaseHeading(ticket: SupportTicketRow): string {
  const label =
    ticket.clientCaseLabel ||
    (ticket.clientCaseNumber != null
      ? String(ticket.clientCaseNumber).padStart(2, "0")
      : null);
  if (label) return `Case: ${label}`;
  return "Case";
}

interface SupportCasesPanelProps {
  masterAccountId: string;
  clientName?: string | null;
  /** Parent owns Close Support Case modal (sibling of Client Details) to avoid nested stacking. */
  onRequestClose: (ticket: SupportTicketRow) => void;
  /**
   * Exact-Case deep link (staff email → `…&case=KK-######`): auto-expand that
   * Case's conversation and switch the filter so it is visible. Rendered only
   * if the authorized list contains it.
   */
  deepLinkCaseId?: string | null;
  /** Parent owns Impersonate-into-Case modal (reuses canonical SA→MA impersonation). */
  onRequestImpersonateIntoCase: (ticket: SupportTicketRow) => void;
  /** When false, Impersonate into Case is shown disabled (no owner membership). */
  canImpersonate?: boolean;
  /** Optional content rendered at the bottom of the Support Cases card. */
  footer?: React.ReactNode;
  /** Rendered in the card header strip, left of the case count (e.g. Support Mode). */
  headerAction?: React.ReactNode;
}

/** Card frame (icon header strip + "N cases") shared by loading / error / list states. */
function SupportCasesCard({
  count,
  footer,
  headerAction,
  children,
}: {
  count: number | null;
  footer?: React.ReactNode;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <DetailSection
      title="Support Cases"
      icon="bi bi-headset"
      headerRight={
        headerAction || count != null ? (
          <>
            {headerAction}
            {count == null ? null : (
              <span data-testid="support-cases-total">
                {count} case{count === 1 ? "" : "s"}
              </span>
            )}
          </>
        ) : null
      }
      data-testid="client-support-cases-section"
    >
      {children}
      {footer ? <div className="mt-3">{footer}</div> : null}
    </DetailSection>
  );
}

export default function SupportCasesPanel({
  masterAccountId,
  clientName,
  onRequestClose,
  onRequestImpersonateIntoCase,
  canImpersonate = true,
  footer,
  headerAction,
  deepLinkCaseId = null,
}: SupportCasesPanelProps) {
  const [expandedTicketId, setExpandedTicketId] = useState<string | null>(null);
  const [listFilter, setListFilter] = useState<SupportCaseListFilter>("all");
  const appliedDeepLinkCaseRef = useRef<string | null>(null);
  const deepLinkCardRef = useRef<HTMLDivElement | null>(null);
  const { data, isFetching, isError, error, refetch } =
    useListSupportTicketsQuery(
      { masterAccountId },
      // Live refresh of the Case list while the panel is open (new Cases,
      // Open → Closed, closure metadata, CSAT, counts / filter membership) —
      // RTK Query polling + refetch-on-focus, no new subsystem. Polling is
      // paused only while the tab is hidden and resumes with a refetch on focus.
      {
        pollingInterval: SUPPORT_LIST_POLL_MS,
        skipPollingIfUnfocused: true,
        refetchOnFocus: true,
      },
    );

  const canonicalCases = useMemo(
    () => uniqueCanonicalSupportCases(data?.supportTickets),
    [data?.supportTickets],
  );

  const buckets = useMemo(
    () => countSupportCaseBuckets(canonicalCases),
    [canonicalCases],
  );

  // Filter membership then view-specific ordering (FIX 6): never the Case: NN sequence.
  const tickets = useMemo(
    () =>
      sortSupportCasesForView(
        filterSupportCasesByBucket(canonicalCases, listFilter),
        listFilter,
      ),
    [canonicalCases, listFilter],
  );

  // Exact-Case deep link: once the authorized list contains the Case, expand
  // it (conversation open), make its bucket visible, and scroll to it. Applied
  // once per Case id; never mutates the Case.
  useEffect(() => {
    if (!deepLinkCaseId) {
      appliedDeepLinkCaseRef.current = null;
      return;
    }
    if (appliedDeepLinkCaseRef.current === deepLinkCaseId) return;
    const match = canonicalCases.find((c) => c.ticketId === deepLinkCaseId);
    if (!match) return;
    appliedDeepLinkCaseRef.current = deepLinkCaseId;
    setListFilter(isOpenSupportStatus(match.status) ? "open" : "closed");
    setExpandedTicketId(deepLinkCaseId);
  }, [deepLinkCaseId, canonicalCases]);

  useEffect(() => {
    if (!deepLinkCaseId || expandedTicketId !== deepLinkCaseId) return;
    const el = deepLinkCardRef.current;
    if (!el || typeof el.scrollIntoView !== "function") return;
    const t = window.setTimeout(
      () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
      300,
    );
    return () => window.clearTimeout(t);
  }, [deepLinkCaseId, expandedTicketId, listFilter]);

  if (isFetching && !data) {
    return (
      <SupportCasesCard count={null} footer={footer} headerAction={headerAction}>
        <div id="client-support-cases" className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" />
          <span className="small text-muted">Loading support cases…</span>
        </div>
      </SupportCasesCard>
    );
  }

  if (isError) {
    const message =
      (error as { data?: { message?: string } })?.data?.message ||
      (error as { message?: string })?.message ||
      "Failed to load support cases";
    return (
      <SupportCasesCard count={null} footer={footer} headerAction={headerAction}>
        <Alert id="client-support-cases" variant="warning" className="mb-0 py-2">
          {message}{" "}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 align-baseline"
            onClick={() => void refetch()}
          >
            Retry
          </button>
        </Alert>
      </SupportCasesCard>
    );
  }

  // Header (title + count) is rendered by SupportCasesCard; body keeps its original tree.
  const body = (
    <div id="client-support-cases">
      <div
        className="d-flex flex-wrap align-items-center gap-2 mb-3 small"
        role="tablist"
        aria-label="Support case filters"
      >
        <button
          type="button"
          role="tab"
          aria-selected={listFilter === "all"}
          className={`btn btn-sm ${listFilter === "all" ? "btn-dark" : "btn-outline-secondary"}`}
          onClick={() => setListFilter("all")}
          data-testid="support-filter-all"
        >
          All ({buckets.total})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={listFilter === "open"}
          className={`btn btn-sm ${listFilter === "open" ? "btn-dark" : "btn-outline-secondary"}`}
          onClick={() => setListFilter("open")}
          data-testid="support-filter-open"
        >
          Open ({buckets.open})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={listFilter === "closed"}
          className={`btn btn-sm ${listFilter === "closed" ? "btn-dark" : "btn-outline-secondary"}`}
          onClick={() => setListFilter("closed")}
          data-testid="support-filter-closed"
        >
          Closed ({buckets.closed})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={listFilter === "lowSatisfaction"}
          className={`btn btn-sm ${listFilter === "lowSatisfaction" ? "btn-danger" : "btn-outline-danger"}`}
          onClick={() => setListFilter("lowSatisfaction")}
          title="Closed Cases rated 1–2 by the Client — follow up suggested"
          data-testid="support-filter-low-satisfaction"
        >
          Low Satisfaction ({buckets.lowCsat})
        </button>
      </div>
      {tickets.length === 0 ? (
        <p className="small text-muted mb-0" data-testid="support-cases-empty">
          {canonicalCases.length === 0
            ? `No support cases for ${clientName || "this Client"} yet.`
            : listFilter === "lowSatisfaction"
              ? "No low-satisfaction (1–2) ratings — nothing to follow up."
              : `No ${listFilter === "open" ? "open" : "closed"} support cases.`}
        </p>
      ) : (
        <div className="d-flex flex-column gap-3">
          {tickets.map((ticket) => {
            const closed = isClosedSupportStatus(ticket.status);
            const clientClosed = closed && isClientClosedCase(ticket);
            const canClose = isOpenSupportStatus(ticket.status);
            const closedBy = saClosedByLabel(ticket);
            const closedAt = ticketClosedAt(ticket);
            const closureNote = ticketClosureNote(ticket);
            const lowCsat =
              Boolean(ticket.csatLowSatisfaction) || isLowCsat(ticket.csatRating);
            const isDeepLinkTarget = deepLinkCaseId === ticket.ticketId;
            return (
              <div
                key={ticket.ticketId}
                ref={isDeepLinkTarget ? deepLinkCardRef : null}
                className={`border rounded p-3 bg-white${isDeepLinkTarget ? " border-primary" : ""}`}
                data-testid={`support-case-${ticket.ticketId}`}
                data-deep-link={isDeepLinkTarget ? "true" : undefined}
                data-expanded={expandedTicketId === ticket.ticketId ? "true" : "false"}
              >
                <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <div className="d-flex flex-column">
                      <strong>{formatClientCaseHeading(ticket)}</strong>
                      <span className="font-monospace small">
                        Case Number: {ticket.ticketId}
                      </span>
                    </div>
                    <span className="badge text-bg-light border">
                      {formatSupportStatusLabel(ticket.status)}
                    </span>
                    {lowCsat ? (
                      <span
                        className="badge text-bg-danger"
                        title={`Client rated this closed Case ${ticket.csatRating}/5`}
                        data-testid={`support-low-csat-${ticket.ticketId}`}
                      >
                        Low satisfaction {ticket.csatRating}/5
                      </span>
                    ) : ticket.csatRating ? (
                      <span
                        className="badge text-bg-light border"
                        title="Client satisfaction rating"
                      >
                        CSAT {ticket.csatRating}/5
                      </span>
                    ) : null}
                  </div>
                  <span className="small text-muted">
                    {closed && closedAt
                      ? `Closed ${formatSupportWhen(closedAt)}`
                      : `Updated ${formatSupportWhen(ticket.updatedAt || ticket.createdAt)}`}
                  </span>
                </div>
                {ticket.brandName ? (
                  <div className="small mb-2">
                    <span className="text-muted">Brand:</span> {ticket.brandName}
                  </div>
                ) : null}
                <div className="mb-2">
                  <div
                    className="text-uppercase text-muted fw-semibold"
                    style={{ fontSize: 11 }}
                  >
                    Support request
                  </div>
                  <div className="small" style={{ whiteSpace: "pre-wrap" }}>
                    {ticket.summary || "—"}
                  </div>
                </div>
                {closed ? (
                  <div
                    data-testid={`support-closure-${ticket.ticketId}`}
                    data-closed-by-type={clientClosed ? "client" : "staff"}
                  >
                    {/* Client-initiated closure has no staff Closure Note — never manufacture one. */}
                    {clientClosed ? (
                      <div className="mb-2 small text-muted fst-italic">
                        Closed by the Client from Master Account (no staff closure note).
                      </div>
                    ) : (
                      <div className="mb-2">
                        <div
                          className="text-uppercase text-muted fw-semibold"
                          style={{ fontSize: 11 }}
                        >
                          Closure note
                        </div>
                        <div
                          className="small rounded p-2"
                          style={{
                            background: "#f7f7f7",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {closureNote || "No closure note provided."}
                        </div>
                      </div>
                    )}
                    <div className="small text-muted d-flex flex-wrap gap-3">
                      {closedBy ? <span>Closed by {closedBy}</span> : null}
                      {closedAt ? (
                        <span>Closed {formatSupportWhen(closedAt)}</span>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                {canClose ? (
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() =>
                        setExpandedTicketId((cur) =>
                          cur === ticket.ticketId ? null : ticket.ticketId,
                        )
                      }
                    >
                      {expandedTicketId === ticket.ticketId
                        ? "Hide conversation"
                        : "View conversation"}
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={!canImpersonate}
                      title={
                        canImpersonate
                          ? "Open Master Account Support Mode on this case"
                          : "This client has no owner membership to impersonate"
                      }
                      data-testid={`impersonate-into-case-${ticket.ticketId}`}
                      onClick={() => onRequestImpersonateIntoCase(ticket)}
                    >
                      Impersonate into Case
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => onRequestClose(ticket)}
                      data-testid={`close-support-case-${ticket.ticketId}`}
                    >
                      Close Support Case
                    </Button>
                  </div>
                ) : (
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() =>
                        setExpandedTicketId((cur) =>
                          cur === ticket.ticketId ? null : ticket.ticketId,
                        )
                      }
                    >
                      {expandedTicketId === ticket.ticketId
                        ? "Hide conversation"
                        : "View conversation"}
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={!canImpersonate}
                      title={
                        canImpersonate
                          ? "Open Master Account Support Mode on this case (does not reopen)"
                          : "This client has no owner membership to impersonate"
                      }
                      data-testid={`impersonate-into-case-${ticket.ticketId}`}
                      onClick={() => onRequestImpersonateIntoCase(ticket)}
                    >
                      Impersonate into Case
                    </Button>
                  </div>
                )}
                {expandedTicketId === ticket.ticketId ? (
                  <SupportCaseThread
                    masterAccountId={masterAccountId}
                    ticketId={ticket.ticketId}
                    closed={closed}
                    csatSubmittedAt={ticket.csatSubmittedAt ?? null}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <SupportCasesCard count={buckets.total} footer={footer} headerAction={headerAction}>
      {body}
    </SupportCasesCard>
  );
}
